import React, { useMemo } from 'react';
import { Box, Flex, Text, Icon } from '@chakra-ui/react';
import { FiChevronRight, FiCheckCircle } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import InlineCategoryPicker from '../shared/InlineCategoryPicker';
import InlineTagPicker from '../shared/InlineTagPicker';
import CompactMovementRow from '../shared/CompactMovementRow';
import MovementAlertIcon from '../movements/MovementAlertIcon';
import { usePreferences } from '../../contexts/PreferencesContext';
import { getMovementAlerts } from '../../utils/movementAlerts';
import { groupMovementsByDate } from '../../utils/groupMovementsByDate';
import { cardStyle, cardTitle, viewAllStyle } from './dashboardStyles';

const INCOMPLETE_VISIBLE_LIMIT = 9;

/**
 * Incomplete Movements card — shows movements with missing account/category
 * grouped by date. Supports inline category/tag editing and click-to-detail.
 */
export default function DashboardIncompleteMovements({
  movements, categories, allTags, onCategoryChange, onTagsChange, onViewAll, onMovementClick,
}) {
  const { t } = useTranslation('dashboard');
  const { alerts: prefs } = usePreferences();
  // Honor the user's per-alert display prefs: only keep movements that still
  // have at least one enabled alert (label text is irrelevant here, only count).
  const reviewable = useMemo(
    () => (movements || []).filter(m => getMovementAlerts(m, { t: (k) => k, prefs }).length > 0),
    [movements, prefs]
  );
  const total = reviewable.length;
  const visible = useMemo(() => reviewable.slice(0, INCOMPLETE_VISIBLE_LIMIT), [reviewable]);

  const grouped = useMemo(() => groupMovementsByDate(visible, 'day'), [visible]);

  return (
    <Box {...cardStyle} display="flex" flexDirection="column" minH="300px">
      <Flex justify="space-between" align="center" mb={4}>
        <Text {...cardTitle}>{t('incompleteMovements.title')}</Text>
        <Text {...viewAllStyle} onClick={onViewAll}>{t('incompleteMovements.viewAll')} <Icon as={FiChevronRight} w="14px" h="14px" /></Text>
      </Flex>
      {grouped.length > 0 ? (
        <>
          <Box flex={1} position={{ base: 'static', lg: 'relative' }} minH={0}>
            <Box
              position={{ base: 'static', lg: 'absolute' }}
              inset={{ lg: 0 }}
              overflowY={{ base: 'visible', lg: 'auto' }}
              pr={1}
              sx={{
                scrollbarWidth: 'thin',
                scrollbarColor: 'var(--chakra-colors-redesign-border) transparent',
                '&::-webkit-scrollbar': { width: '4px' },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': { background: 'var(--chakra-colors-redesign-border)', borderRadius: '2px' },
                '&::-webkit-scrollbar-thumb:hover': { background: 'var(--chakra-colors-redesign-border-strong)' },
              }}
            >
            {grouped.map(group => (
              <Box key={group.label} mb={2}>
                <Text textStyle="microLabel" mt={2} mb={1}>{group.label}</Text>
                <Flex direction="column" gap={0}>
                  {group.items.map(m => (
                    <CompactMovementRow
                      key={m.id}
                      leading={<MovementAlertIcon movement={m} boxSize="0.7rem" />}
                      note={m.note || m.description || t('incompleteMovements.noDescription')}
                      tags={<InlineTagPicker movement={m} allTags={allTags} onTagsChange={onTagsChange} />}
                      category={<InlineCategoryPicker movement={m} categories={categories} onCategoryChange={onCategoryChange} maxW="100%" variant="pill" />}
                      amount={m.amount}
                      currency={m.currency}
                      onClick={() => onMovementClick(m.id)}
                    />
                  ))}
                </Flex>
              </Box>
            ))}
            </Box>
          </Box>
          <Box borderTop="1px solid" borderColor="redesign.border" mt={3} mx={{ base: -3, md: -5 }} mb={{ base: -3, md: -5 }} px={{ base: 3, md: 5 }} py={3}>
            <Flex justify="space-between" align="center">
              <Text fontSize="md" color="redesign.text.muted">{t('incompleteMovements.count', { visible: visible.length, total })}</Text>
              <Flex as="button" align="center" gap={2} px={3} py="6px" borderRadius="lg" border="1px solid" borderColor="redesign.border" cursor="pointer" _hover={{ bg: 'redesign.bg.hover' }} onClick={onViewAll}>
                <Icon as={FiCheckCircle} w="12px" h="12px" color="redesign.text.muted" />
                <Text fontSize="sm" fontWeight={500} color="redesign.text">{t('incompleteMovements.review')}</Text>
              </Flex>
            </Flex>
          </Box>
        </>
      ) : (
        <Flex direction="column" align="center" justify="center" py={8} gap={2}>
          <Icon as={FiCheckCircle} w="32px" h="32px" color="redesign.accent.green" />
          <Text fontSize="md" color="redesign.text.muted">{t('incompleteMovements.allComplete')}</Text>
        </Flex>
      )}
    </Box>
  );
}
